import type {
  JournalEntry,
  JournalEntryLine,
  ChartOfAccount,
  AgingBucket,
  EntityAgingReportItem,
  CustomerInvoice,
  VendorBill,
  SalesOrder,
  PurchaseOrder,
  ItemReceipt,
  BankTransaction,
  BankReconciliationRecord,
  FinancialPeriod,
  Currency,
  ExchangeRate,
  CostCenter,
  RecurringSchedule,
  RecurringJournalTemplate,
  InterCompanyTransfer,
} from './accounting.schema'

export class AccountingService {
  /**
   * Validate double-entry equality with 2-decimal ₱ PHP precision.
   * Also ensures if currency is specified, foreign amounts are calculated properly.
   * If validCostCenterIds is provided, validates that any line with a costCenterId uses a valid one.
   */
  static validateJournalEntry(
    lines: Array<{ debitAmount?: number; creditAmount?: number; currency?: string; exchangeRate?: number; foreignAmount?: number; costCenterId?: string }>,
    validCostCenterIds?: string[]
  ): { valid: boolean; reason?: string } {
    let totalDebit = 0
    let totalCredit = 0

    for (const line of lines) {
      const debit = line.debitAmount ?? 0
      const credit = line.creditAmount ?? 0
      totalDebit += debit
      totalCredit += credit

      if (line.currency && line.currency !== 'PHP' && line.exchangeRate && line.foreignAmount) {
        const expectedAmount = Math.round(line.foreignAmount * line.exchangeRate * 100) / 100
        const actualAmount = debit > 0 ? debit : credit
        if (Math.abs(expectedAmount - actualAmount) > 0.01) {
          return { valid: false, reason: `Foreign amount mismatch on line: ${line.foreignAmount} * ${line.exchangeRate} != ${actualAmount}` }
        }
      }

      if (line.costCenterId && validCostCenterIds) {
        if (!validCostCenterIds.includes(line.costCenterId)) {
          return { valid: false, reason: `Invalid cost center ID: ${line.costCenterId}` }
        }
      }
    }

    totalDebit = Math.round(totalDebit * 100) / 100
    totalCredit = Math.round(totalCredit * 100) / 100

    if (totalDebit !== totalCredit) {
      return { valid: false, reason: `Debits (₱${totalDebit.toFixed(2)}) must equal Credits (₱${totalCredit.toFixed(2)})` }
    }

    if (totalDebit === 0) {
      return { valid: false, reason: 'Journal entry cannot have zero amount' }
    }

    return { valid: true }
  }

  // ─── Cost Centers ──────────────────────────────────────────────

  static createCostCenter(data: Partial<CostCenter>): CostCenter {
    return {
      id: data.id || `cc-${Date.now()}`,
      tenantId: data.tenantId || 'default',
      code: data.code || `CC-${Date.now()}`,
      name: data.name || '',
      parentId: data.parentId,
      isActive: data.isActive ?? true,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
  }

  static updateCostCenter(existing: CostCenter, updates: Partial<CostCenter>): CostCenter {
    return {
      ...existing,
      ...updates,
      updatedAt: Date.now(),
    }
  }

  static getCostCenters(tenantId: string, allCostCenters: CostCenter[]): CostCenter[] {
    return allCostCenters.filter(cc => cc.tenantId === tenantId)
  }

  // ─── Multi-Currency ──────────────────────────────────────────

  static addCurrency(data: Partial<Currency>): Currency {
    return {
      id: data.id || `cur-${Date.now()}`,
      code: data.code || 'PHP',
      name: data.name || 'Philippine Peso',
      symbol: data.symbol || '₱',
      isBaseCurrency: data.isBaseCurrency ?? false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
  }

  static setExchangeRate(baseCurrency: string, targetCurrency: string, rate: number, effectiveDate: number = Date.now()): ExchangeRate {
    return {
      id: `fx-${Date.now()}`,
      baseCurrency,
      targetCurrency,
      rate,
      effectiveDate,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
  }

  static getExchangeRate(rates: ExchangeRate[], targetCurrency: string, date: number = Date.now()): number {
    if (targetCurrency === 'PHP') return 1
    const sortedRates = [...rates]
      .filter(r => r.targetCurrency === targetCurrency && r.effectiveDate <= date)
      .sort((a, b) => b.effectiveDate - a.effectiveDate)
    return sortedRates.length > 0 ? (sortedRates[0]?.rate ?? 1) : 1
  }

  /**
   * Compute trial balance from active chart of accounts and journal entry lines.
   */
  static computeTrialBalance(accounts: ChartOfAccount[], lines: JournalEntryLine[]): Array<{ code: string; name: string; debit: number; credit: number; balance: number }> {
    const balances = new Map<string, { debit: number; credit: number }>()

    for (const line of lines) {
      if (!balances.has(line.accountCode)) {
        balances.set(line.accountCode, { debit: 0, credit: 0 })
      }
      const b = balances.get(line.accountCode)!
      b.debit += line.debitAmount ?? 0
      b.credit += line.creditAmount ?? 0
    }

    return accounts
      .filter(a => !a.isHeader)
      .map(account => {
        const b = balances.get(account.code) ?? { debit: 0, credit: 0 }
        const balance = account.normalBalance === 'debit' ? b.debit - b.credit : b.credit - b.debit
        return {
          code: account.code,
          name: account.name,
          debit: Math.round(b.debit * 100) / 100,
          credit: Math.round(b.credit * 100) / 100,
          balance: Math.round(balance * 100) / 100,
        }
      })
  }

  /**
   * Compute General Ledger account register with running balance.
   */
  static computeGeneralLedgerAccount(
    account: ChartOfAccount,
    lines: Array<JournalEntryLine & { entryDate: number; referenceNumber: string; memo?: string }>,
    beginningBalance: number = 0
  ): {
    accountCode: string
    accountName: string
    beginningBalance: number
    totalDebit: number
    totalCredit: number
    endingBalance: number
    transactions: Array<{
      date: number
      referenceNumber: string
      memo: string
      debit: number
      credit: number
      runningBalance: number
    }>
  } {
    const sorted = [...lines].sort((a, b) => a.entryDate - b.entryDate)
    let runningBalance = beginningBalance
    let totalDebit = 0
    let totalCredit = 0

    const transactions = sorted.map(line => {
      const debit = line.debitAmount ?? 0
      const credit = line.creditAmount ?? 0
      totalDebit += debit
      totalCredit += credit

      if (account.normalBalance === 'debit') {
        runningBalance += debit - credit
      } else {
        runningBalance += credit - debit
      }

      return {
        date: line.entryDate,
        referenceNumber: line.referenceNumber,
        memo: line.description || line.memo || '',
        debit: Math.round(debit * 100) / 100,
        credit: Math.round(credit * 100) / 100,
        runningBalance: Math.round(runningBalance * 100) / 100,
      }
    })

    return {
      accountCode: account.code,
      accountName: account.name,
      beginningBalance: Math.round(beginningBalance * 100) / 100,
      totalDebit: Math.round(totalDebit * 100) / 100,
      totalCredit: Math.round(totalCredit * 100) / 100,
      endingBalance: Math.round(runningBalance * 100) / 100,
      transactions,
    }
  }

  /**
   * Calculate Aging Buckets for AR (Invoices) or AP (Bills).
   */
  static calculateAgingBuckets(
    items: Array<{ id: string; name: string; balanceDue: number; dueDate: number }>,
    asOfDate: number = Date.now()
  ): {
    summary: AgingBucket
    byEntity: EntityAgingReportItem[]
  } {
    const DAY_MS = 24 * 60 * 60 * 1000

    const summary: AgingBucket = {
      current: 0,
      days1To30: 0,
      days31To60: 0,
      days61To90: 0,
      daysOver90: 0,
      total: 0,
    }

    const entityMap = new Map<string, { name: string; aging: AgingBucket }>()

    for (const item of items) {
      if (item.balanceDue <= 0) continue

      const daysOverdue = Math.floor((asOfDate - item.dueDate) / DAY_MS)

      if (!entityMap.has(item.id)) {
        entityMap.set(item.id, {
          name: item.name,
          aging: { current: 0, days1To30: 0, days31To60: 0, days61To90: 0, daysOver90: 0, total: 0 },
        })
      }

      const entity = entityMap.get(item.id)!
      const amount = item.balanceDue

      if (daysOverdue <= 0) {
        summary.current += amount
        entity.aging.current += amount
      } else if (daysOverdue <= 30) {
        summary.days1To30 += amount
        entity.aging.days1To30 += amount
      } else if (daysOverdue <= 60) {
        summary.days31To60 += amount
        entity.aging.days31To60 += amount
      } else if (daysOverdue <= 90) {
        summary.days61To90 += amount
        entity.aging.days61To90 += amount
      } else {
        summary.daysOver90 += amount
        entity.aging.daysOver90 += amount
      }

      summary.total += amount
      entity.aging.total += amount
    }

    // Round all figures
    const roundBucket = (b: AgingBucket): AgingBucket => ({
      current: Math.round(b.current * 100) / 100,
      days1To30: Math.round(b.days1To30 * 100) / 100,
      days31To60: Math.round(b.days31To60 * 100) / 100,
      days61To90: Math.round(b.days61To90 * 100) / 100,
      daysOver90: Math.round(b.daysOver90 * 100) / 100,
      total: Math.round(b.total * 100) / 100,
    })

    const byEntity: EntityAgingReportItem[] = Array.from(entityMap.entries()).map(([id, val]) => ({
      id,
      name: val.name,
      aging: roundBucket(val.aging),
    }))

    return {
      summary: roundBucket(summary),
      byEntity,
    }
  }

  /**
   * Generate balanced double-entry journal lines for a Customer Invoice.
   * Debit: Accounts Receivable (1100)
   * Credit: Sales Revenue (4000/line.accountCode) + Output VAT Payable (2100) if applicable
   */
  static generateCustomerInvoiceJournal(
    invoice: CustomerInvoice,
    arAccountCode: string = '1100',
    vatAccountCode: string = '2100'
  ): {
    description: string
    lines: Array<{ accountCode: string; debitAmount?: number; creditAmount?: number; description?: string }>
  } {
    const lines: Array<{ accountCode: string; debitAmount?: number; creditAmount?: number; description?: string }> = []

    // Debit AR for full invoice total
    lines.push({
      accountCode: arAccountCode,
      debitAmount: Math.round(invoice.totalAmount * 100) / 100,
      creditAmount: 0,
      description: `Invoice #${invoice.invoiceNumber} - ${invoice.customerName}`,
    })

    // Credit revenue lines
    for (const line of invoice.lines) {
      lines.push({
        accountCode: line.accountCode || '4000',
        debitAmount: 0,
        creditAmount: Math.round(line.amount * 100) / 100,
        description: line.description,
      })
    }

    // Credit VAT payable if any
    if (invoice.taxAmount > 0) {
      lines.push({
        accountCode: vatAccountCode,
        debitAmount: 0,
        creditAmount: Math.round(invoice.taxAmount * 100) / 100,
        description: `Output VAT for Invoice #${invoice.invoiceNumber}`,
      })
    }

    return {
      description: `Sales Invoice #${invoice.invoiceNumber} - ${invoice.customerName}`,
      lines,
    }
  }

  /**
   * Generate balanced double-entry journal lines for a Vendor Bill.
   * Debit: Expense/Inventory accounts
   * Credit: Accounts Payable (2000)
   */
  static generateVendorBillJournal(
    bill: VendorBill,
    apAccountCode: string = '2000',
    inputVatAccountCode: string = '1350',
    withholdingTaxAccountCode: string = '2150'
  ): {
    description: string
    lines: Array<{ accountCode: string; debitAmount?: number; creditAmount?: number; description?: string }>
  } {
    const lines: Array<{ accountCode: string; debitAmount?: number; creditAmount?: number; description?: string }> = []
    let totalWithholding = 0

    // Debit expense/inventory lines
    for (const line of bill.lines) {
      lines.push({
        accountCode: line.accountCode || '5000',
        debitAmount: Math.round(line.amount * 100) / 100,
        creditAmount: 0,
        description: line.description,
      })
      if (line.withholdingTaxRate && line.withholdingTaxRate > 0) {
        totalWithholding += line.amount * (line.withholdingTaxRate / 100)
      }
    }

    totalWithholding = Math.round(totalWithholding * 100) / 100

    // Debit Input VAT if any
    if (bill.taxAmount > 0) {
      lines.push({
        accountCode: inputVatAccountCode,
        debitAmount: Math.round(bill.taxAmount * 100) / 100,
        creditAmount: 0,
        description: `Input VAT for Bill #${bill.billNumber}`,
      })
    }

    if (totalWithholding > 0) {
      lines.push({
        accountCode: withholdingTaxAccountCode,
        debitAmount: 0,
        creditAmount: totalWithholding,
        description: `Expanded Withholding Tax for Bill #${bill.billNumber}`,
      })
    }

    // Credit Accounts Payable
    lines.push({
      accountCode: apAccountCode,
      debitAmount: 0,
      creditAmount: Math.round((bill.totalAmount - totalWithholding) * 100) / 100,
      description: `Bill #${bill.billNumber} - ${bill.vendorName}`,
    })

    return {
      description: `Vendor Bill #${bill.billNumber} - ${bill.vendorName}`,
      lines,
    }
  }

  // ─── Order & Invoice Management ──────────────────────────────

  static createSalesOrder(data: Partial<SalesOrder>): SalesOrder {
    const lines = data.lines || []
    let subtotal = 0
    let taxAmount = 0

    for (const line of lines) {
      const amount = line.quantity * line.unitPrice
      subtotal += amount
      if (line.taxRatePercent) {
        taxAmount += amount * (line.taxRatePercent / 100)
      }
    }

    subtotal = Math.round(subtotal * 100) / 100
    taxAmount = Math.round(taxAmount * 100) / 100
    const totalAmount = subtotal + taxAmount

    return {
      id: data.id || `so-${Date.now()}`,
      tenantId: data.tenantId || 'default',
      soNumber: data.soNumber || `SO-${Date.now()}`,
      customerId: data.customerId || '',
      customerName: data.customerName || '',
      orderDate: data.orderDate || Date.now(),
      expectedDeliveryDate: data.expectedDeliveryDate,
      subtotal,
      taxAmount,
      totalAmount,
      status: data.status || 'draft',
      notes: data.notes,
      lines,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null,
    }
  }

  static confirmSalesOrder(so: SalesOrder): SalesOrder {
    return { ...so, status: 'confirmed', updatedAt: Date.now() }
  }

  static createInvoiceFromSO(so: SalesOrder, birOfficialReceipt?: string): CustomerInvoice {
    const lines = so.lines.map(line => ({
      id: line.id,
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      discountPercent: line.discountPercent,
      taxRatePercent: line.taxRatePercent,
      accountCode: '4010', // Default Sales Revenue
      amount: line.amount || (line.quantity * line.unitPrice),
    }))

    return {
      id: `inv-${Date.now()}`,
      tenantId: so.tenantId,
      invoiceNumber: `INV-${Date.now()}`,
      salesOrderId: so.id,
      birOfficialReceipt,
      customerId: so.customerId,
      customerName: so.customerName,
      invoiceDate: Date.now(),
      dueDate: Date.now() + 30 * 24 * 60 * 60 * 1000, // +30 days default
      subtotal: so.subtotal,
      taxAmount: so.taxAmount,
      totalAmount: so.totalAmount,
      balanceDue: so.totalAmount,
      status: 'unpaid',
      lines,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null,
    }
  }

  static applyPaymentToInvoice(invoice: CustomerInvoice, amount: number): CustomerInvoice {
    const newBalance = Math.max(0, invoice.balanceDue - amount)
    let status = invoice.status
    if (newBalance === 0) status = 'paid'
    else if (newBalance < invoice.totalAmount) status = 'partially_paid'

    return {
      ...invoice,
      balanceDue: Math.round(newBalance * 100) / 100,
      status,
      updatedAt: Date.now(),
    }
  }

  static createPurchaseOrder(data: Partial<PurchaseOrder>): PurchaseOrder {
    const lines = data.lines || []
    let subtotal = 0
    let taxAmount = 0

    for (const line of lines) {
      const amount = line.quantity * line.unitPrice
      subtotal += amount
      if (line.taxRatePercent) {
        taxAmount += amount * (line.taxRatePercent / 100)
      }
    }

    subtotal = Math.round(subtotal * 100) / 100
    taxAmount = Math.round(taxAmount * 100) / 100
    const totalAmount = subtotal + taxAmount

    return {
      id: data.id || `po-${Date.now()}`,
      tenantId: data.tenantId || 'default',
      poNumber: data.poNumber || `PO-${Date.now()}`,
      vendorId: data.vendorId || '',
      vendorName: data.vendorName || '',
      orderDate: data.orderDate || Date.now(),
      expectedDeliveryDate: data.expectedDeliveryDate,
      subtotal,
      taxAmount,
      totalAmount,
      status: data.status || 'draft',
      notes: data.notes,
      lines,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null,
    }
  }

  static receivePurchaseOrder(po: PurchaseOrder, receivedLines: Array<{ itemId: string; quantityReceived: number }>): ItemReceipt {
    const lines = receivedLines.map((rl, idx) => {
      const poLine = po.lines.find(l => l.itemId === rl.itemId)
      if (!poLine) throw new Error(`Item ${rl.itemId} not in PO`)
      if (rl.quantityReceived > poLine.quantity) throw new Error(`Cannot receive more than ordered for ${rl.itemId}`)
      return {
        id: `ir-line-${idx}`,
        itemId: rl.itemId,
        description: poLine.description,
        quantityReceived: rl.quantityReceived
      }
    })

    return {
      id: `ir-${Date.now()}`,
      tenantId: po.tenantId,
      receiptNumber: `IR-${Date.now()}`,
      purchaseOrderId: po.id,
      vendorId: po.vendorId,
      vendorName: po.vendorName,
      receiptDate: Date.now(),
      lines,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null
    }
  }

  static createBillFromPO(po: PurchaseOrder, receipt?: ItemReceipt): VendorBill {
    const billLines: any[] = []
    let subtotal = 0
    let taxAmount = 0
    
    for (const poLine of po.lines) {
      let qtyToBill = poLine.quantity
      if (receipt) {
        const rLine = receipt.lines.find(l => l.itemId === poLine.itemId)
        qtyToBill = rLine ? rLine.quantityReceived : 0
      }
      if (qtyToBill <= 0) continue

      const amount = qtyToBill * poLine.unitPrice
      subtotal += amount
      if (poLine.taxRatePercent) {
        taxAmount += amount * (poLine.taxRatePercent / 100)
      }

      billLines.push({
        id: poLine.id,
        description: poLine.description,
        quantity: qtyToBill,
        unitPrice: poLine.unitPrice,
        accountCode: '1200', // Default Inventory or expense
        amount: Math.round(amount * 100) / 100,
      })
    }

    subtotal = Math.round(subtotal * 100) / 100
    taxAmount = Math.round(taxAmount * 100) / 100
    const totalAmount = subtotal + taxAmount

    return {
      id: `bill-${Date.now()}`,
      tenantId: po.tenantId,
      billNumber: `BILL-${Date.now()}`,
      purchaseOrderId: po.id,
      vendorId: po.vendorId,
      vendorName: po.vendorName,
      billDate: Date.now(),
      dueDate: Date.now() + 30 * 24 * 60 * 60 * 1000,
      subtotal,
      taxAmount,
      totalAmount,
      balanceDue: totalAmount,
      status: 'pending_approval',
      lines: billLines,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null,
    }
  }


  /**
   * Bank Reconciliation engine: compares bank statement balance with book ledger.
   */
  static reconcileBankStatement(
    statementEndingBalance: number,
    bookEndingBalance: number,
    bankTransactions: BankTransaction[],
    unclearedChecksTotal: number = 0,
    depositsInTransitTotal: number = 0
  ): BankReconciliationRecord {
    // Adjusted Bank Balance = Statement Ending Balance + Deposits in Transit - Outstanding/Uncleared Checks
    const adjustedBankBalance = statementEndingBalance + depositsInTransitTotal - unclearedChecksTotal
    const clearedTotal = bankTransactions.filter(t => t.isCleared).reduce((sum, t) => sum + t.amount, 0)
    
    // Difference between Adjusted Bank Balance and Book Balance
    const difference = Math.round((adjustedBankBalance - bookEndingBalance) * 100) / 100
    const isReconciled = Math.abs(difference) === 0

    return {
      id: `recon-${Date.now()}`,
      tenantId: 'default',
      bankAccountCode: '1020',
      statementDate: Date.now(),
      statementEndingBalance: Math.round(statementEndingBalance * 100) / 100,
      clearedBalance: Math.round(clearedTotal * 100) / 100,
      bookBalance: Math.round(bookEndingBalance * 100) / 100,
      difference,
      isReconciled,
    }
  }

  /**
   * Check if a financial date falls within a closed financial period.
   */
  static isPeriodLocked(transactionDate: number, closedPeriods: FinancialPeriod[]): boolean {
    return closedPeriods.some(
      period => period.isClosed && transactionDate >= period.periodStart && transactionDate <= period.periodEnd
    )
  }

  /**
   * Generate Standard Enterprise Chart of Accounts (QuickBooks Enterprise & CDA Standard).
   */
  static getDefaultChartOfAccounts(tenantId: string = 'default'): Array<Omit<ChartOfAccount, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'version' | 'createdBy' | 'updatedBy'>> {
    return [
      // Assets
      { tenantId, code: '1000', name: 'CURRENT ASSETS', accountType: 'asset', normalBalance: 'debit', isHeader: true, is_active: true },
      { tenantId, code: '1010', name: 'Cash on Hand / Petty Cash', accountType: 'asset', subtype: 'cash_and_bank', normalBalance: 'debit', parentCode: '1000', is_active: true },
      { tenantId, code: '1020', name: 'Cash in Bank - Operating', accountType: 'asset', subtype: 'cash_and_bank', normalBalance: 'debit', parentCode: '1000', is_active: true },
      { tenantId, code: '1100', name: 'Accounts Receivable', accountType: 'asset', subtype: 'accounts_receivable', normalBalance: 'debit', parentCode: '1000', is_active: true },
      { tenantId, code: '1150', name: 'Allowance for Doubtful Accounts', accountType: 'asset', subtype: 'allowance_doubtful_accounts', normalBalance: 'credit', parentCode: '1000', is_active: true },
      { tenantId, code: '1200', name: 'Merchandise Inventory', accountType: 'asset', subtype: 'inventory', normalBalance: 'debit', parentCode: '1000', is_active: true },
      { tenantId, code: '1300', name: 'Prepaid Expenses', accountType: 'asset', subtype: 'prepaid_expenses', normalBalance: 'debit', parentCode: '1000', is_active: true },
      { tenantId, code: '1350', name: 'Input VAT', accountType: 'asset', subtype: 'other_current_asset', normalBalance: 'debit', parentCode: '1000', is_active: true },
      
      { tenantId, code: '1500', name: 'NON-CURRENT ASSETS', accountType: 'asset', normalBalance: 'debit', isHeader: true, is_active: true },
      { tenantId, code: '1510', name: 'Property, Plant and Equipment', accountType: 'asset', subtype: 'fixed_asset', normalBalance: 'debit', parentCode: '1500', is_active: true },
      { tenantId, code: '1520', name: 'Accumulated Depreciation - PPE', accountType: 'asset', subtype: 'accumulated_depreciation', normalBalance: 'credit', parentCode: '1500', is_active: true },

      // Liabilities
      { tenantId, code: '2000', name: 'CURRENT LIABILITIES', accountType: 'liability', normalBalance: 'credit', isHeader: true, is_active: true },
      { tenantId, code: '2010', name: 'Accounts Payable', accountType: 'liability', subtype: 'accounts_payable', normalBalance: 'credit', parentCode: '2000', is_active: true },
      { tenantId, code: '2020', name: 'Accrued Expenses Payable', accountType: 'liability', subtype: 'accrued_expenses', normalBalance: 'credit', parentCode: '2000', is_active: true },
      { tenantId, code: '2100', name: 'Output VAT Payable', accountType: 'liability', subtype: 'taxes_payable', normalBalance: 'credit', parentCode: '2000', is_active: true },
      { tenantId, code: '2110', name: 'Withholding Tax Payable', accountType: 'liability', subtype: 'taxes_payable', normalBalance: 'credit', parentCode: '2000', is_active: true },
      { tenantId, code: '2200', name: 'Notes & Loans Payable (Current)', accountType: 'liability', subtype: 'short_term_debt', normalBalance: 'credit', parentCode: '2000', is_active: true },

      { tenantId, code: '2500', name: 'NON-CURRENT LIABILITIES', accountType: 'liability', normalBalance: 'credit', isHeader: true, is_active: true },
      { tenantId, code: '2510', name: 'Long-Term Notes Payable', accountType: 'liability', subtype: 'long_term_debt', normalBalance: 'credit', parentCode: '2500', is_active: true },

      // Equity
      { tenantId, code: '3000', name: 'EQUITY', accountType: 'equity', normalBalance: 'credit', isHeader: true, is_active: true },
      { tenantId, code: '3010', name: "Owner's Capital / Share Capital", accountType: 'equity', subtype: 'owners_capital', normalBalance: 'credit', parentCode: '3000', is_active: true },
      { tenantId, code: '3020', name: "Owner's Drawings", accountType: 'equity', subtype: 'owners_drawings', normalBalance: 'debit', parentCode: '3000', is_active: true },
      { tenantId, code: '3100', name: 'Retained Earnings / Undivided Net Surplus', accountType: 'equity', subtype: 'retained_earnings', normalBalance: 'credit', parentCode: '3000', is_active: true },
      { tenantId, code: '3200', name: 'Statutory Reserve Fund', accountType: 'equity', subtype: 'statutory_reserve', normalBalance: 'credit', parentCode: '3000', is_active: true },

      // Revenue
      { tenantId, code: '4000', name: 'OPERATING REVENUE', accountType: 'income', normalBalance: 'credit', isHeader: true, is_active: true },
      { tenantId, code: '4010', name: 'Sales Revenue', accountType: 'income', subtype: 'operating_revenue', normalBalance: 'credit', parentCode: '4000', is_active: true },
      { tenantId, code: '4020', name: 'Service Revenue & Professional Fees', accountType: 'income', subtype: 'service_revenue', normalBalance: 'credit', parentCode: '4000', is_active: true },
      { tenantId, code: '4030', name: 'Sales Discounts & Allowances', accountType: 'income', subtype: 'sales_discount', normalBalance: 'debit', parentCode: '4000', is_active: true },
      { tenantId, code: '4100', name: 'Interest & Other Income', accountType: 'income', subtype: 'other_income', normalBalance: 'credit', parentCode: '4000', is_active: true },

      // Expenses
      { tenantId, code: '5000', name: 'COST OF GOODS SOLD', accountType: 'expense', subtype: 'cost_of_goods_sold', normalBalance: 'debit', is_active: true },
      { tenantId, code: '6000', name: 'OPERATING EXPENSES', accountType: 'expense', normalBalance: 'debit', isHeader: true, is_active: true },
      { tenantId, code: '6010', name: 'Salaries and Wages', accountType: 'expense', subtype: 'salaries_and_wages', normalBalance: 'debit', parentCode: '6000', is_active: true },
      { tenantId, code: '6020', name: 'Rent Expense', accountType: 'expense', subtype: 'rent_expense', normalBalance: 'debit', parentCode: '6000', is_active: true },
      { tenantId, code: '6030', name: 'Utilities Expense', accountType: 'expense', subtype: 'utilities_expense', normalBalance: 'debit', parentCode: '6000', is_active: true },
      { tenantId, code: '6040', name: 'Depreciation Expense', accountType: 'expense', subtype: 'depreciation_expense', normalBalance: 'debit', parentCode: '6000', is_active: true },
      { tenantId, code: '6050', name: 'Office Supplies & Materials', accountType: 'expense', subtype: 'office_supplies', normalBalance: 'debit', parentCode: '6000', is_active: true },
      { tenantId, code: '6060', name: 'Professional & Legal Fees', accountType: 'expense', subtype: 'professional_fees', normalBalance: 'debit', parentCode: '6000', is_active: true },
      { tenantId, code: '6070', name: 'Taxes and Licenses', accountType: 'expense', subtype: 'taxes_and_licenses', normalBalance: 'debit', parentCode: '6000', is_active: true },
      { tenantId, code: '7000', name: 'Bank Service Charges & Interest', accountType: 'expense', subtype: 'bank_service_charges', normalBalance: 'debit', is_active: true },
    ]
  }

  // ─── Bank Import & Auto-Categorization ─────────────────────────────────

  static parseBankCsv(
    csvText: string,
    columnMap: { date: number; description: number; amount: number; reference?: number },
    hasHeader: boolean = true
  ): import('./accounting.schema').CsvBankTransaction[] {
    const lines = csvText.split('\n').map(l => l.trim()).filter(l => l)
    const transactions: import('./accounting.schema').CsvBankTransaction[] = []
    const startIndex = hasHeader ? 1 : 0

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i]
      if (!line) continue
      // Simple CSV split handling quotes (basic)
      const cols = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(c => c.replace(/^"|"$/g, '').trim())
      
      const dateStr = cols[columnMap.date] || ''
      const desc = cols[columnMap.description] || ''
      
      let amount = 0
      const amountStr = cols[columnMap.amount]
      if (amountStr) {
        amount = parseFloat(amountStr.replace(/[^0-9.-]/g, ''))
      }

      const ref = columnMap.reference !== undefined ? (cols[columnMap.reference] || '') : ''

      if (dateStr && desc) {
        transactions.push({
          date: dateStr,
          description: desc,
          amount,
          reference: ref,
          rawRow: line,
        })
      }
    }
    return transactions
  }

  static buildDefaultBankRules(tenantId: string, bankAccountId: string): import('./accounting.schema').BankImportRule[] {
    return [
      { id: `rule-${Date.now()}-1`, tenantId, bankAccountId, matchField: 'description', matchPattern: 'SSS|SOCIAL SECURITY', accountCode: '2200', description: 'SSS Payable', priority: 10, isActive: true }, // SSS Payable
      { id: `rule-${Date.now()}-2`, tenantId, bankAccountId, matchField: 'description', matchPattern: 'PHILHEALTH', accountCode: '2210', description: 'PhilHealth Payable', priority: 10, isActive: true },
      { id: `rule-${Date.now()}-3`, tenantId, bankAccountId, matchField: 'description', matchPattern: 'PAGIBIG|HDMF', accountCode: '2220', description: 'Pag-IBIG Payable', priority: 10, isActive: true },
      { id: `rule-${Date.now()}-4`, tenantId, bankAccountId, matchField: 'description', matchPattern: 'BIR|BUREAU OF INTERNAL REVENUE', accountCode: '2100', description: 'Tax Payable', priority: 10, isActive: true },
      { id: `rule-${Date.now()}-5`, tenantId, bankAccountId, matchField: 'description', matchPattern: 'SALARY|PAYROLL', accountCode: '6010', description: 'Salaries Expense', priority: 20, isActive: true },
      { id: `rule-${Date.now()}-6`, tenantId, bankAccountId, matchField: 'description', matchPattern: 'MERALCO|MAYNILAD|MANILA WATER', accountCode: '6030', description: 'Utilities Expense', priority: 20, isActive: true },
    ]
  }

  static applyImportRules(
    transaction: import('./accounting.schema').CsvBankTransaction,
    rules: import('./accounting.schema').BankImportRule[]
  ): string | null {
    const activeRules = rules.filter(r => r.isActive).sort((a, b) => b.priority - a.priority)

    for (const rule of activeRules) {
      if (rule.matchField === 'description') {
        const regex = new RegExp(rule.matchPattern, 'i')
        if (regex.test(transaction.description)) {
          return rule.accountCode
        }
      } else if (rule.matchField === 'amount') {
        // e.g. amount > 1000 or specific amount (could be extended)
        if (transaction.amount.toString() === rule.matchPattern) {
          return rule.accountCode
        }
      }
    }
    return null
  }

  // ─── Recurring Journal Entries ─────────────────────────────────

  static createRecurringTemplate(data: Partial<RecurringJournalTemplate>): RecurringJournalTemplate {
    return {
      id: data.id || `rjt-${Date.now()}`,
      tenantId: data.tenantId || 'default',
      templateName: data.templateName || 'Recurring Template',
      schedule: data.schedule || 'monthly',
      nextRunDate: data.nextRunDate || Date.now(),
      endDate: data.endDate,
      lines: data.lines || [],
      autoPost: data.autoPost ?? false,
      isActive: data.isActive ?? true,
      lastRunDate: data.lastRunDate,
      description: data.description,
    }
  }

  static computeNextRunDate(currentRunDate: number, schedule: RecurringSchedule): number {
    const d = new Date(currentRunDate)
    switch (schedule) {
      case 'daily':
        d.setDate(d.getDate() + 1)
        break
      case 'weekly':
        d.setDate(d.getDate() + 7)
        break
      case 'monthly':
        d.setMonth(d.getMonth() + 1)
        break
      case 'quarterly':
        d.setMonth(d.getMonth() + 3)
        break
      case 'annually':
        d.setFullYear(d.getFullYear() + 1)
        break
    }
    return d.getTime()
  }

  static processRecurringJournalTemplates(
    templates: RecurringJournalTemplate[],
    asOfDate: number
  ): { generatedEntries: Array<JournalEntry & { lines: JournalEntryLine[] }>; updatedTemplates: RecurringJournalTemplate[] } {
    const generatedEntries: Array<JournalEntry & { lines: JournalEntryLine[] }> = []
    const updatedTemplates: RecurringJournalTemplate[] = []

    for (const template of templates) {
      if (!template.isActive) continue
      if (template.endDate && asOfDate > template.endDate && template.nextRunDate > template.endDate) continue
      
      let nextRunDate = template.nextRunDate
      let lastRunDate = template.lastRunDate
      let hasGenerated = false
      let currentTemplate = { ...template }

      while (nextRunDate <= asOfDate) {
        if (template.endDate && nextRunDate > template.endDate) break

        let totalDebit = 0
        let totalCredit = 0
        const entryLines: JournalEntryLine[] = template.lines.map((l, idx) => {
          totalDebit += l.debitAmount || 0
          totalCredit += l.creditAmount || 0
          return {
            ...l,
            id: `line-${Date.now()}-${idx}`,
            journalEntryId: '',
            createdAt: asOfDate,
            updatedAt: asOfDate,
            deletedAt: null,
          }
        })

        const entryId = `je-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        entryLines.forEach(l => l.journalEntryId = entryId)

        const entry = {
          id: entryId,
          tenantId: template.tenantId,
          entryDate: nextRunDate,
          referenceNumber: `REC-${template.id}-${nextRunDate}`,
          description: template.description || `Generated from ${template.templateName}`,
          entryType: 'auto' as const,
          status: (template.autoPost ? 'posted' : 'draft') as import('./accounting.schema').EntryStatus,
          totalDebit,
          totalCredit,
          createdAt: asOfDate,
          updatedAt: asOfDate,
          deletedAt: null,
          version: 1,
          createdBy: 'system',
          updatedBy: 'system',
          lines: entryLines,
        }
        
        generatedEntries.push(entry)
        hasGenerated = true
        lastRunDate = nextRunDate
        nextRunDate = this.computeNextRunDate(nextRunDate, template.schedule)
      }

      if (hasGenerated) {
        currentTemplate.lastRunDate = lastRunDate
        currentTemplate.nextRunDate = nextRunDate
        updatedTemplates.push(currentTemplate)
      }
    }

    return { generatedEntries, updatedTemplates }
  }

  // ─── Inter-Company Transactions ────────────────────────────────

  static createInterCompanyTransfer(data: Omit<InterCompanyTransfer, 'id' | 'status' | 'createdAt'>): { 
    transfer: InterCompanyTransfer; 
    sourceEntry: JournalEntry & { lines: JournalEntryLine[] }; 
    targetEntry: JournalEntry & { lines: JournalEntryLine[] } 
  } {
    const now = Date.now()
    const transfer: InterCompanyTransfer = {
      ...data,
      id: `ict-${now}`,
      status: 'completed',
      createdAt: now,
    }

    const sourceEntryId = `je-src-${now}`
    const sourceEntry: JournalEntry & { lines: JournalEntryLine[] } = {
      id: sourceEntryId,
      tenantId: data.sourceTenantId,
      entryDate: now,
      referenceNumber: data.referenceNumber,
      description: data.description,
      entryType: 'manual' as const,
      status: 'posted' as import('./accounting.schema').EntryStatus,
      totalDebit: data.amount,
      totalCredit: data.amount,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      version: 1,
      createdBy: 'system',
      updatedBy: 'system',
      lines: [
        {
          id: `line-src-1-${now}`,
          journalEntryId: sourceEntryId,
          accountCode: data.dueFromAccountCode,
          debitAmount: data.amount,
          creditAmount: 0,
          description: `Due from ${data.targetTenantId}`,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
        },
        {
          id: `line-src-2-${now}`,
          journalEntryId: sourceEntryId,
          accountCode: data.sourceAccountCode,
          debitAmount: 0,
          creditAmount: data.amount,
          description: data.description,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
        }
      ]
    }

    const targetEntryId = `je-tgt-${now}`
    const targetEntry: JournalEntry & { lines: JournalEntryLine[] } = {
      id: targetEntryId,
      tenantId: data.targetTenantId,
      entryDate: now,
      referenceNumber: data.referenceNumber,
      description: data.description,
      entryType: 'manual' as const,
      status: 'posted' as import('./accounting.schema').EntryStatus,
      totalDebit: data.amount,
      totalCredit: data.amount,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      version: 1,
      createdBy: 'system',
      updatedBy: 'system',
      lines: [
        {
          id: `line-tgt-1-${now}`,
          journalEntryId: targetEntryId,
          accountCode: data.targetAccountCode,
          debitAmount: data.amount,
          creditAmount: 0,
          description: data.description,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
        },
        {
          id: `line-tgt-2-${now}`,
          journalEntryId: targetEntryId,
          accountCode: data.dueToAccountCode,
          debitAmount: 0,
          creditAmount: data.amount,
          description: `Due to ${data.sourceTenantId}`,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
        }
      ]
    }

    return { transfer, sourceEntry, targetEntry }
  }

  static trainCategorizationModel(historicalTransactions: import('./accounting.schema').BankTransaction[]): Map<string, Record<string, number>> {
    const model = new Map<string, Record<string, number>>()
    const stopWords = new Set(['the', 'and', 'a', 'to', 'of', 'in', 'i', 'is', 'that', 'it', 'on', 'you', 'this', 'for', 'at', 'with', 'do', 'are', 'as', 'by', 'an', 'was', 'be', 'or', 'from'])
    
    for (const tx of historicalTransactions) {
      if (!tx.matchedJournalLineId) continue
      
      const category = tx.matchedJournalLineId
      if (!model.has(category)) {
        model.set(category, {})
      }
      const wordCounts = model.get(category)!
      
      const tokens = tx.description
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(t => t.length > 1 && !stopWords.has(t))
        
      for (const token of tokens) {
        wordCounts[token] = (wordCounts[token] || 0) + 1
      }
    }
    
    return model
  }

  static predictCategoryML(description: string, model: Map<string, Record<string, number>>): { accountCode: string, confidence: number } | null {
    if (model.size === 0) return null

    const stopWords = new Set(['the', 'and', 'a', 'to', 'of', 'in', 'i', 'is', 'that', 'it', 'on', 'you', 'this', 'for', 'at', 'with', 'do', 'are', 'as', 'by', 'an', 'was', 'be', 'or', 'from'])
    const tokens = description
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(t => t.length > 1 && !stopWords.has(t))

    if (tokens.length === 0) return null

    let bestCategory: string | null = null
    let bestScore = -1

    // Calculate total documents/occurrences per category for Naive Bayes prior
    let totalOccurrences = 0
    const categoryTotals = new Map<string, number>()
    for (const [category, words] of model.entries()) {
      let sum = 0
      for (const count of Object.values(words)) {
        sum += count
      }
      categoryTotals.set(category, sum)
      totalOccurrences += sum
    }
    
    if (totalOccurrences === 0) return null

    const categoryScores = new Map<string, number>()

    for (const [category, words] of model.entries()) {
      const categoryTotal = categoryTotals.get(category) || 0
      let score = categoryTotal / totalOccurrences // Prior probability
      
      for (const token of tokens) {
        const wordCount = words[token] || 0
        // Simple smoothing
        const likelihood = (wordCount + 1) / (categoryTotal + Object.keys(words).length + 1)
        score *= likelihood
      }
      
      categoryScores.set(category, score)
      if (score > bestScore) {
        bestScore = score
        bestCategory = category
      }
    }

    if (!bestCategory) return null

    // Normalize confidence to 0-1 range
    let totalScore = 0
    for (const score of categoryScores.values()) {
      totalScore += score
    }

    const confidence = totalScore > 0 ? bestScore / totalScore : 0
    return { accountCode: bestCategory, confidence }
  }

  static autoCategorizeBankImport(
    transactions: import('./accounting.schema').CsvBankTransaction[],
    rules: import('./accounting.schema').BankImportRule[],
    existingAccounts: import('./accounting.schema').ChartOfAccount[],
    tenantId: string,
    bankAccountCode: string,
    model?: Map<string, Record<string, number>>
  ): { matched: import('./accounting.schema').BankTransaction[]; unmatched: import('./accounting.schema').CsvBankTransaction[] } {
    const matched: import('./accounting.schema').BankTransaction[] = []
    const unmatched: import('./accounting.schema').CsvBankTransaction[] = []
    const accountCodeSet = new Set(existingAccounts.map(a => a.code))

    for (const t of transactions) {
      let accountCode = this.applyImportRules(t, rules)
      let confidence = 1.0

      if (!accountCode && model) {
        const mlResult = this.predictCategoryML(t.description, model)
        if (mlResult && mlResult.confidence > 0.5) {
          accountCode = mlResult.accountCode
          confidence = mlResult.confidence
        }
      }

      if (accountCode && accountCodeSet.has(accountCode)) {
        matched.push({
          id: `bt-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          tenantId,
          bankAccountCode,
          transactionDate: new Date(t.date).getTime() || Date.now(),
          reference: t.reference,
          description: t.description,
          amount: t.amount,
          isCleared: false,
          matchedJournalLineId: accountCode, // Temporary store account code here for later creation of journal line
        })
      } else {
        unmatched.push(t)
      }
    }

    return { matched, unmatched }
  }

  static reconcilePaymentWithTolerance(
    invoice: import('./accounting.schema').CustomerInvoice,
    paymentAmount: number,
    config: {
      toleranceLimit: number
      roundingExpenseCode: string
      roundingIncomeCode: string
      bankAccountCode: string
      arAccountCode: string
    }
  ): {
    isFullySettled: boolean
    openBalance: number
    writeOffAmount: number
    journalEntry: import('./accounting.schema').JournalEntry & { lines: import('./accounting.schema').JournalEntryLine[] }
  } {
    const diff = invoice.balanceDue - paymentAmount
    const absDiff = Math.abs(diff)

    let isFullySettled = false
    let openBalance = diff
    let writeOffAmount = 0
    let roundingLine: Partial<import('./accounting.schema').JournalEntryLine> | null = null

    // Exact match or within tolerance
    if (absDiff <= config.toleranceLimit) {
      isFullySettled = true
      openBalance = 0
      writeOffAmount = diff

      if (diff > 0) {
        // Underpaid: Difference goes to Expense (Debit)
        roundingLine = {
          accountCode: config.roundingExpenseCode,
          debitAmount: diff,
          creditAmount: 0,
          description: `Rounding Expense for Invoice ${invoice.invoiceNumber}`
        }
      } else if (diff < 0) {
        // Overpaid: Difference goes to Income (Credit)
        roundingLine = {
          accountCode: config.roundingIncomeCode,
          debitAmount: 0,
          creditAmount: absDiff,
          description: `Rounding Income for Invoice ${invoice.invoiceNumber}`
        }
      }
    } else {
      // Payment beyond tolerance is just a partial payment (or overpayment left on account)
      isFullySettled = openBalance <= 0
    }

    const journalEntryId = `je-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    
    // Always Debit Cash (or Bank) for paymentAmount
    const bankLine: import('./accounting.schema').JournalEntryLine = {
      id: `jl-${Date.now()}-1`,
      journalEntryId,
      accountCode: config.bankAccountCode,
      debitAmount: paymentAmount,
      creditAmount: 0,
      description: `Payment received for Invoice ${invoice.invoiceNumber}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null
    }

    // Always Credit AR for the amount being settled (payment + writeOff)
    const settledAmount = paymentAmount + (isFullySettled ? writeOffAmount : 0)
    const arLine: import('./accounting.schema').JournalEntryLine = {
      id: `jl-${Date.now()}-2`,
      journalEntryId,
      accountCode: config.arAccountCode,
      debitAmount: 0,
      creditAmount: settledAmount,
      description: `AR settlement for Invoice ${invoice.invoiceNumber}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null
    }

    const lines: import('./accounting.schema').JournalEntryLine[] = [bankLine, arLine]

    if (roundingLine) {
      lines.push({
        ...roundingLine,
        id: `jl-${Date.now()}-3`,
        journalEntryId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        deletedAt: null
      } as import('./accounting.schema').JournalEntryLine)
    }

    const journalEntry: import('./accounting.schema').JournalEntry & { lines: import('./accounting.schema').JournalEntryLine[] } = {
      id: journalEntryId,
      tenantId: invoice.tenantId,
      entryDate: Date.now(),
      referenceNumber: `PAY-${invoice.invoiceNumber}`,
      description: `Payment Reconciliation for Invoice ${invoice.invoiceNumber}`,
      entryType: 'auto',
      status: 'posted' as import('./accounting.schema').EntryStatus,
      isPosted: true,
      totalDebit: paymentAmount + (diff > 0 && isFullySettled ? diff : 0),
      totalCredit: settledAmount + (diff < 0 && isFullySettled ? absDiff : 0),
      version: 1,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null,
      createdBy: 'system',
      updatedBy: 'system',
      lines
    }

    return {
      isFullySettled,
      openBalance,
      writeOffAmount,
      journalEntry
    }
  }
}
